import { ONE_HOUR_MS } from '@eflight/shared';

import { setConsumption } from '../../../Datasource/CountableChanges/CountableChange';
import {
    AIRCRAFT_COUNTABLE_FLIGHT_TIME,
    AIRCRAFT_COUNTABLE_TACH_TIME,
    countableTypeIsFuel,
} from '../../../Datasource/CountableChanges/types';
import { type StartedFlightLeg } from '../../../Datasource/FlightLegs/FlightLeg';
import { getMidnightDate, setTimesToStartDate } from '../../../utils/timeUtils';
import { type FinishLegState } from '../../Flight/Leg/finish/types';

export type OfpData = {
    airplane?: string; // back-compat, todo: remove
    registration?: string;
    airportOffIcao?: string;
    airportOnIcao?: string;
    timeOut?: string;
    timeOff?: string;
    timeOn?: string;
    timeIn?: string;
    fuelBefore?: string;
    fuelUsed?: string;
    tachAfter?: string;
};
type SavedOfpData = {
    time: number;
    aircraftId: string;
    data: OfpData;
};
const SESSION_STORAGE_KEY = 'ofp-data';
const DATA_MAX_AGE = 10 * 60 * 1e3; // 10 minutes
export const saveExternalOfpData = (aircraftId: string, data: any) => {
    const toSave: SavedOfpData = {
        time: Date.now(),
        aircraftId,
        data,
    };
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(toSave));
};

export const clearExternalOfpData = () => {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
};
export const getExternalOfpData = (aircraftId: string, airportOffIcao?: string): OfpData | null => {
    const retrieved = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!retrieved) {
        return null;
    }
    try {
        const parsed = JSON.parse(retrieved) as SavedOfpData;
        if (Date.now() - parsed.time > DATA_MAX_AGE) {
            return null;
        }
        if (parsed.aircraftId !== aircraftId) {
            return null;
        }
        if (airportOffIcao && parsed.data.airportOffIcao?.toUpperCase() !== airportOffIcao) {
            return null;
        }
        return parsed.data;
    } catch (e) {
        console.error('Error retrieving external OFP data', e);
        return null;
    }
};

// plain decimal only: parseFloat would take '12:30' as 12 and 'abc' as NaN
const parseOfpNumber = (value: string | undefined): number | undefined =>
    value !== undefined && /^\d+(\.\d+)?$/.test(value.trim()) ? Number(value) : undefined;

export const mergeExternalOfpDataAndFinishLegState = (
    externalData: OfpData,
    finishLeg: FinishLegState,
    startLeg: StartedFlightLeg
): void => {
    if (externalData.airportOnIcao) {
        finishLeg.airportOnIcao = externalData.airportOnIcao;
    }
    const timeFields: Array<'timeIn' | 'timeOff' | 'timeOn' | 'timeOut'> = ['timeOut', 'timeOff', 'timeOn', 'timeIn'];
    for (const timeField of timeFields) {
        const externalTime = externalData[timeField];
        if (externalTime?.match(/^([01]\d|2[0-3])[0-5]\d$/)) {
            const externalHours = parseInt(externalTime.substr(0, 2), 10);
            const externalMinutes = parseInt(externalTime.substr(2, 2), 10);
            const date = new Date(startLeg.timeOut.getTime());
            date.setUTCHours(externalHours);
            date.setUTCMinutes(externalMinutes);
            finishLeg[timeField] = date;
        }
    }
    setTimesToStartDate(
        getMidnightDate(startLeg.createdAt),
        finishLeg.timeOut,
        finishLeg.timeOff,
        finishLeg.timeOn,
        finishLeg.timeIn
    );
    let { countableChangeConsumed } = finishLeg;
    const fuelIndex = countableChangeConsumed.countableTypes.findIndex(countableTypeIsFuel);
    if (fuelIndex >= 0) {
        const fuelType = countableChangeConsumed.countableTypes[fuelIndex];
        const fuelBefore = parseOfpNumber(externalData.fuelBefore);
        const fuelUsed = parseOfpNumber(externalData.fuelUsed);
        if (fuelBefore !== undefined) {
            const countableValuesBefore = [...countableChangeConsumed.countableValuesBefore];
            countableValuesBefore[fuelIndex] = fuelBefore;
            countableChangeConsumed = { ...countableChangeConsumed, countableValuesBefore };
        }
        if (fuelUsed !== undefined) {
            countableChangeConsumed = setConsumption(countableChangeConsumed, fuelType, fuelUsed * -1);
        } else if (fuelBefore !== undefined) {
            // keep the existing consumption, so the after value follows the new before value
            const changeIndex = countableChangeConsumed.changeTypes.indexOf(fuelType);
            countableChangeConsumed = setConsumption(
                countableChangeConsumed,
                fuelType,
                changeIndex >= 0 ? countableChangeConsumed.changeValues[changeIndex] : null
            );
        }
    }
    const tachIndex = countableChangeConsumed.countableTypes.indexOf(AIRCRAFT_COUNTABLE_TACH_TIME);
    const tachAfter = parseOfpNumber(externalData.tachAfter);
    if (tachIndex >= 0 && tachAfter !== undefined) {
        const before = countableChangeConsumed.countableValuesBefore[tachIndex];
        const change = tachAfter - before;
        countableChangeConsumed = setConsumption(
            countableChangeConsumed,
            countableChangeConsumed.countableTypes[tachIndex],
            change
        );
    }
    const flightTimeIndex = countableChangeConsumed.countableTypes.indexOf(AIRCRAFT_COUNTABLE_FLIGHT_TIME);
    if (flightTimeIndex >= 0 && finishLeg.timeOff && finishLeg.timeOn) {
        const timeDiffMs = finishLeg.timeOn.getTime() - finishLeg.timeOff.getTime();
        const timeDiffHours = timeDiffMs / ONE_HOUR_MS;
        countableChangeConsumed = setConsumption(
            countableChangeConsumed,
            AIRCRAFT_COUNTABLE_FLIGHT_TIME,
            timeDiffHours
        );
    }
    finishLeg.countableChangeConsumed = countableChangeConsumed;
};
